import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Activity,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const Trading = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [accountSummary, setAccountSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [tradeForm, setTradeForm] = useState({
    symbol: '',
    quantity: '',
    price_per_share: '',
    transaction_type: 'BUY'
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 10;

  useEffect(() => {
    fetchPortfolio();
    fetchAccountSummary();
    fetchTransactions();
    fetchStocks();
    fetchPendingOrders();
    fetchOrderHistory();
    fetchMarketStatus();
    const interval = setInterval(() => {
      fetchPendingOrders();
      fetchOrderHistory();
      fetchMarketStatus();
    }, 25000);
    return () => clearInterval(interval);
  }, []);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trading/portfolio/');
      if (response.data.success) {
        setPortfolio(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      setMessage({ type: 'error', text: 'Failed to fetch portfolio data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountSummary = async () => {
    try {
      const response = await api.get('/trading/account/summary/');
      if (response.data.success) {
        setAccountSummary(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching account summary:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const response = await api.get('/trading/transactions/');
      if (response.data.success) {
        setTransactions(response.data.data);
        // Debug print all transactions
        console.log('Fetched transactions:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchStocks = async () => {
    try {
      const response = await api.get('/trading/stocks/');
      if (response.data.success) {
        setStocks(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const response = await api.get('/trading/orders/pending/');
      // For each order, fetch latest price to show live and drive UX
      const orders = Array.isArray(response.data) ? response.data : response.data?.results || [];
      const withLive = await Promise.all(orders.map(async (o) => {
        try {
          const baseSymbol = (o.symbol || o.stock?.symbol || '').replace('.L', '');
          const prof = await api.get(`/market-data/company-profile/`, { params: { symbol: baseSymbol } });
          return { ...o, current_price: prof?.data?.price ?? o.current_price };
        } catch {
          return o;
        }
      }));
      
      // Check if any orders were cancelled (count decreased)
      const previousCount = pendingOrders.length;
      const newCount = withLive.length;
      
      // Auto-cancellation removed - orders will remain pending
      
      setPendingOrders(withLive);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    }
  };

  const fetchOrderHistory = async () => {
    try {
      const response = await api.get('/trading/orders/history/');
      setOrderHistory(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (error) {
      console.error('Error fetching order history:', error);
    }
  };

  const fetchMarketStatus = async () => {
    try {
      const response = await api.get('/trading/market/status/');
      if (response.data.success) {
        setMarketStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching market status:', error);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await api.post(`/trading/orders/${orderId}/cancel/`);
      await fetchPendingOrders();
      await fetchOrderHistory();
    } catch (error) {
      console.error('Error canceling order:', error);
    }
  };

  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
    setTradeForm({
      ...tradeForm,
      symbol: stock.symbol,
      price_per_share: stock.current_price
    });
  };

  const handleTradeFormChange = (e) => {
    const { name, value } = e.target;
    setTradeForm({
      ...tradeForm,
      [name]: value
    });
  };

  const executeTrade = async (e) => {
    e.preventDefault();
    
    if (!tradeForm.symbol || !tradeForm.quantity || !tradeForm.price_per_share) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/trading/trade/execute/', tradeForm);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        
        // Update portfolio and account data
        await fetchPortfolio();
        await fetchAccountSummary();
        await fetchTransactions();
        
        // Highlight the most recent transaction
        setTimeout(() => {
          if (response.data.transaction_id) {
            setHighlightedTransactionId(response.data.transaction_id);
            setTimeout(() => setHighlightedTransactionId(null), 3000);
          }
        }, 500);

        // Reset form
        setTradeForm({
          symbol: '',
          quantity: '',
          price_per_share: '',
          transaction_type: 'BUY'
        });
        setSelectedStock(null);
      } else {
        setMessage({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      console.error('Trade execution error:', error);
      let backendError = error?.response?.data?.error;
      setMessage({ type: 'error', text: backendError || 'Trade execution failed' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '+0.00%';
    return `${value >= 0 ? '+' : ''}${value?.toFixed(2) || '0.00'}%`;
  };

  const totalPages = Math.ceil(transactions.length / transactionsPerPage);
  const paginatedTransactions = transactions.slice((currentPage - 1) * transactionsPerPage, currentPage * transactionsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  useEffect(() => { setCurrentPage(1); }, [transactions]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <BarChart3 className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Order Book</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Track your trading activity and transaction history</p>
            </div>
          </div>
        </div>

        {/* Market Status */}
        {marketStatus && (
          <div className="mb-6">
            <div className={`rounded-lg p-4 border ${
              marketStatus.market_open 
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' 
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    marketStatus.market_open 
                      ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400' 
                      : 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-400'
                  }`}>
                    <Activity className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${
                      marketStatus.market_open 
                        ? 'text-green-800 dark:text-green-200' 
                        : 'text-red-800 dark:text-red-200'
                    }`}>
                      Market {marketStatus.market_open ? 'Open' : 'Closed'}
                    </h3>
                    <p className={`text-sm ${
                      marketStatus.market_open 
                        ? 'text-green-700 dark:text-green-300' 
                        : 'text-red-700 dark:text-red-300'
                    }`}>
                      {marketStatus.day_of_week} • {marketStatus.london_time} • {marketStatus.market_hours}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${
                    marketStatus.market_open 
                      ? 'text-green-700 dark:text-green-300' 
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    {marketStatus.user_pending_orders} pending orders
                  </p>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 rounded-lg p-4 border ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' 
              : message.type === 'error'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400'
                    : message.type === 'error'
                    ? 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-400'
                    : 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400'
                }`}>
                  {message.type === 'success' ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : message.type === 'error' ? (
                    <TrendingDown className="h-5 w-5" />
                  ) : (
                    <Activity className="h-5 w-5" />
                  )}
                </div>
                <p className={`font-medium ${
                  message.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : message.type === 'error'
                    ? 'text-red-800 dark:text-red-200'
                    : 'text-blue-800 dark:text-blue-200'
                }`}>
                  {message.text}
                </p>
              </div>
              <button
                onClick={() => setMessage({ type: '', text: '' })}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Pending Orders */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Pending Orders</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Orders waiting for execution

                  </p>
                </div>
              </div>
              <button onClick={fetchPendingOrders} className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-200">Refresh</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {pendingOrders.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Symbol</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Side</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Limit Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {pendingOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">{order.symbol || order.stock?.symbol}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{order.side?.toUpperCase()}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{order.quantity}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">
                        <div className="flex flex-col">
                          <span>Limit: {formatCurrency(order.price)}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Live: {formatCurrency(order.current_price ?? 0)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">{new Date(order.created_at).toLocaleString('en-GB', { timeZone: 'Europe/London' })}</td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => cancelOrder(order.id)} className="px-3 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">Cancel</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-6 text-sm text-gray-600 dark:text-gray-400">No pending orders</div>
            )}
          </div>
        </div>

        {/* Order History and Transactions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Transaction History</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your recent trading activity
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      • Includes cancelled orders after market hours
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {transactions.length} transactions
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {transactionsLoading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner />
              </div>
            ) : transactions.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/30">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        P&L
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      </tr>
                    </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedTransactions.map((transaction, index) => (
                      <tr 
                        key={index} 
                        className={`transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                          highlightedTransactionId === transaction.id 
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' 
                            : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {transaction.symbol}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {transaction.transaction_type === 'BUY' ? (
                              <ArrowUpRight className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4 text-red-600 dark:text-red-400 mr-2" />
                            )}
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                              transaction.transaction_type === 'BUY' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}>
                              {transaction.transaction_type}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                          {transaction.quantity?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                          {formatCurrency(transaction.price_per_share)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                          {formatCurrency(transaction.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {transaction.realized_gain !== null && transaction.realized_gain !== undefined ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.realized_gain >= 0 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}>
                              {formatCurrency(transaction.realized_gain)}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          {new Date(transaction.transaction_date).toLocaleString('en-GB', { timeZone: 'Europe/London' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                <div className="flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page {currentPage} of {totalPages}
                    </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
                  </div>
                </div>
              </div>
            </>
            ) : (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Transactions Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Your trading activity will appear here once you start making transactions.
              </p>
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Ready for your first trade</span>
              </div>
              </div>
            )}
        </div>

        {/* Stats Cards - Moved to Bottom */}
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Transactions</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{transactions.length}</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Buy Orders</p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {transactions.filter(t => t.transaction_type === 'BUY').length}
                  </p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Sell Orders</p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {transactions.filter(t => t.transaction_type === 'SELL').length}
                  </p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading; 